import React, { useState, useEffect, useCallback } from 'react';
import { AlertCircle, FileJson, Search } from 'lucide-react';
import legacyUapiService from '../services/LegacyUapiService';
import WizardStepper from './WizardStepper';
import Spinner from './shared/Spinner';
import './AddPaymentPorterWizard.css';
import './UniversalApiPage.css';
import WizardReview from './WizardReview';

// Import one of Oracle's message bodies and say which of our URLs carry it.
//
// THE ORDER IS THE POINT, and it is the order the work is actually done in:
//
//   1  Spec        act.json, rsv.json, crm.json — Oracle publishes one file per module, and choosing it
//                  is choosing the subject. Listed live from the repository, because Oracle adds to it.
//   2  Payload     what that spec publishes. THIS is the payload: one body per path, the resource shape
//                  a GET returns or the document a POST-only endpoint accepts.
//   3  URLs        which of our operations carry it — more than one, normally. OHIP describes a resource
//                  once and its GET, POST and PUT share that body, which is what applies_to records.
//   4  Name        what we call it. The payload's identity, and how the catalogue is read.
//
// This used to start from one of OUR operations and ask which of Oracle's bodies described it. That put
// the module in charge of a document belonging to a spec, and meant reading 58 summaries with nothing to
// judge them against.
const STEPS = [
    { num: 1, label: 'Spec' },
    { num: 2, label: 'Payload' },
    { num: 3, label: 'URLs' },
    { num: 4, label: 'Name' },
];

const kb = (bytes) => `${Math.max(1, Math.round((bytes || 0) / 1024))} KB`;

// `forOperation` opens the wizard already aimed at one of our URLs - the route the payload list says has
// no body. Its module names the spec, so step 1 is answered and skipped, and step 3 arrives with that
// URL already ticked. Everything else is the same wizard; opening it aimed at nothing is still the
// normal path.
export default function UapiPayloadWizard({ onClose, onSaved, forOperation }) {
    const [step, setStep] = useState(1);

    const [specs, setSpecs] = useState([]);
    const [specWarning, setSpecWarning] = useState(null);
    const [spec, setSpec] = useState(null);
    const [specSearch, setSpecSearch] = useState('');

    const [read, setRead] = useState(null);          // what the chosen spec publishes
    // THE PAYLOAD, BY PATH AND END OF THE CALL. A path can publish two documents (V492) — the GET's
    // response and a write's own body — so the path alone no longer identifies one.
    const [picked, setPicked] = useState(null);      // `${operation_path}|${body_source}`
    const [payloadSearch, setPayloadSearch] = useState('');

    const [urls, setUrls] = useState([]);            // our operation ids
    const [urlSearch, setUrlSearch] = useState('');
    // Where the ticks came from: 'spec' (this body's path and methods), 'existing' (the mapping this
    // payload was last saved with) or 'operation' (opened to give one URL a body). The step used to claim
    // the spec every time, which is wrong and unfalsifiable the moment an existing mapping seeds it -
    // and it is what made a screen full of unrelated ticks look like a bug in the matching.
    const [seedSource, setSeedSource] = useState('spec');
    // Methods of this payload's own path that the spec publishes and we do not expose. Ticking one
    // creates it, so the Universal API can pass the call through.
    const [mirror, setMirror] = useState([]);

    const [name, setName] = useState('');

    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const errText = (e, fallback) =>
        e?.response?.data?.error?.message || e?.response?.data?.message || e?.message || fallback;

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const res = await legacyUapiService.listSpecs();
            const d = res.data || res;
            setSpecs(d.specs || []);
            setSpecWarning(d.warning || null);
        } catch (e) {
            setError(errText(e, "Could not list Oracle's specs"));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // Opened for a specific URL: pick its module's spec and read it, so the first thing on screen is the
    // list of bodies rather than 44 file names the reader would have to map back to /rsv themselves.
    // Only once, and only when the module actually names a file - a module with no spec_file drops
    // through to the normal step 1, which is the right place to choose one.
    useEffect(() => {
        if (!forOperation || !specs.length || spec) return;
        const match = specs.find(x => x.name === forOperation.spec_file);
        if (!match) return;
        setSpec(match);
        (async () => {
            setBusy(true);
            try {
                const res = await legacyUapiService.readSpec(match.name);
                setRead(res.data || res);
                setStep(2);
            } catch (e) {
                // Stay on step 1 with the spec selected: the reader can retry with Next, and the error
                // says which file failed rather than leaving a blank step 2.
                setError(errText(e, `Could not read ${match.name}`));
            } finally {
                setBusy(false);
            }
        })();
    }, [forOperation, specs, spec]);   // eslint-disable-line

    // 1 -> 2. The whole file is fetched and every $ref closure resolved, which for blk.json is 2.2 MB of
    // input — so the wait is real and the button says so rather than appearing to have done nothing.
    const readSpec = async () => {
        if (!spec) return;
        setBusy(true);
        setError(null);
        try {
            const res = await legacyUapiService.readSpec(spec.name);
            const d = res.data || res;
            setRead(d);
            setPicked(null);
            setStep(2);
        } catch (e) {
            setError(errText(e, `Could not read ${spec.name}`));
        } finally {
            setBusy(false);
        }
    };

    const keyOf = (p) => `${p.operation_path}|${p.body_source || 'response'}`;
    const chosen = read && read.payloads.find(p => keyOf(p) === picked);

    // What the spec says this body serves, against what we actually expose at that same path. The gap is
    // the pass-through case: OHIP publishes the call, a consumer of ours wants it, and our tree has no
    // route for it.
    const specMethods = String(chosen?.applies_to || '').split(',').filter(Boolean);
    const haveAtPath = (read?.ourOperations || [])
        .filter(o => o.full_path === chosen?.operation_path)
        .map(o => o.method);
    const missingMethods = specMethods.filter(m => !haveAtPath.includes(m));

    // 2 -> 3. Seeded, not blank. A payload already imported comes back with the URLs it was given; a new
    // one is offered the operations whose path matches the spec's and whose method the spec says this
    // body serves. Both are a starting point to correct, which is quicker and more accurate than
    // assembling a set from a list of several hundred.
    // What the SPEC alone implies: this body's own path, and only the methods the spec says carry it.
    // Extracted so the reset button below and the seeding here cannot disagree about what "matching"
    // means.
    // Does the spec say THIS body belongs on THIS call - same path, and a method the spec declares for it?
    const isSpecMatch = (o, payload) => {
        const serves = String(payload?.applies_to || '').split(',').filter(Boolean);
        return o.full_path === payload?.operation_path && serves.includes(o.method);
    };

    const specMatchIds = (payload) => {
        return (read?.ourOperations || [])
            .filter(o => isSpecMatch(o, payload))
            .map(o => String(o.id));
    };

    const goToUrls = () => {
        if (!chosen) return;
        const already = chosen.existing?.operation_ids;
        if (forOperation) {
            // The URL this was opened to fix, plus whatever the payload already served and whatever the
            // spec says shares this body. Its own id is added last and de-duplicated, so it is ticked
            // even when the path match below would not have found it.
            setUrls([...new Set([...(already || []).map(String), ...specMatchIds(chosen), String(forOperation.id)])]);
            setSeedSource('operation');
        } else {
            // THE SPEC MATCH IS THE DEFAULT, even when this body is already saved against other URLs.
            // Pre-ticking a previous mapping made a step full of unrelated ticks look like the matching
            // was broken, when the matching had not run at all. Anything the spec does not match is
            // greyed instead - still selectable, because a payload legitimately serving another URL is
            // the whole point of this step, but never chosen on your behalf.
            setUrls(specMatchIds(chosen));
            setSeedSource(already && already.length ? 'existing' : 'spec');
        }
        // Mirroring is NOT ticked by default. Creating routes in the Universal API is a change to what we
        // publish, and it is not undone by unticking a box afterwards — that is a decision to make on
        // purpose, not one to discover having made.
        setMirror([]);
        setStep(3);
    };

    // 3 -> 4. The spec's summary is a serviceable name and a poor one — "Get an activity" describes the
    // call, not the document — so it is offered as a starting point rather than saved silently.
    const goToName = () => {
        setName(chosen?.existing?.name || chosen?.summary || chosen?.operation_path || '');
        setStep(4);
    };

    const save = async () => {
        setBusy(true);
        setError(null);
        try {
            const res = await legacyUapiService.createPayload({
                specFile: spec.name,
                operationPath: chosen.operation_path,
                bodySource: chosen.body_source || 'response',
                name: name.trim(),
                operationIds: urls,
                mirrorMethods: mirror,
            });
            onSaved(res.data || res);
        } catch (e) {
            setError(errText(e, 'Could not save that payload'));
            setBusy(false);
        }
    };

    const matchesSpec = specs.filter(s => {
        const q = specSearch.trim().toLowerCase();
        if (!q) return true;
        return [s.name, s.module_path].some(v => String(v || '').toLowerCase().includes(q));
    });

    const matchesPayload = (read?.payloads || []).filter(p => {
        const q = payloadSearch.trim().toLowerCase();
        if (!q) return true;
        return [p.operation_path, p.summary, p.operation_id].some(v => String(v || '').toLowerCase().includes(q));
    });

    const matchesUrl = (read?.ourOperations || []).filter(o => {
        const q = urlSearch.trim().toLowerCase();
        if (!q) return true;
        return [o.full_path, o.method, o.description].some(v => String(v || '').toLowerCase().includes(q));
    });

    const selectedUrls = (read?.ourOperations || []).filter(o => urls.includes(String(o.id)));

    return (
        <div className="lu-import-backdrop" onClick={() => !busy && onClose()}>
            <div className="lu-import-modal lu-wizard-modal" onClick={(e) => e.stopPropagation()}>
                <div className="lu-import-head">
                    <h3 className="lu-import-title">Add a payload</h3>
                    <WizardStepper steps={STEPS} currentStep={step} />
                </div>

                {error && (
                    <div className="error-alert" style={{ margin: '0 1.5rem' }}>
                        <div className="error-alert-content">
                            <AlertCircle className="error-alert-icon" />
                            <p className="error-alert-message">{error}</p>
                        </div>
                    </div>
                )}

                {step === 1 && (
                    <>
                        <div className="lu-import-head" style={{ borderTop: 'none', paddingTop: 0 }}>
                            <p className="lu-import-sub">
                                Which of Oracle's specs, from the hospitality-api-docs repository. One file
                                per module — its bodies are the payloads.
                            </p>
                            {specWarning && <p className="lu-payload-notice">{specWarning}</p>}
                            <div className="uapi-search-row">
                                <Search size={15} className="uapi-search-icon" />
                                <input
                                    type="text"
                                    className="pst-hotel-search"
                                    placeholder="Search specs…"
                                    value={specSearch}
                                    onChange={e => setSpecSearch(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="lu-import-list">
                            {loading ? (
                                <div className="loading-section"><Spinner size="md" /><p>Listing specs...</p></div>
                            ) : matchesSpec.length === 0 ? (
                                <p className="lu-empty" style={{ padding: '1.5rem' }}>No spec matches that.</p>
                            ) : matchesSpec.map(s => (
                                <label key={s.name} className={`lu-import-row${spec?.name === s.name ? ' picked' : ''}`}>
                                    <input
                                        type="radio"
                                        name="uapi-spec"
                                        checked={spec?.name === s.name}
                                        onChange={() => setSpec(s)}
                                        disabled={busy}
                                    />
                                    <span className="lu-import-row-main">
                                        <span className="lu-import-op">
                                            <FileJson size={14} /> {s.name}
                                        </span>
                                        <span className="lu-import-summary">
                                            {s.module_path ? `Our ${s.module_path} module` : 'No module of ours names this spec'}
                                            {s.withdrawn && ' · no longer published'}
                                        </span>
                                    </span>
                                    <span className="lu-import-meta">
                                        {s.payloads > 0 && (
                                            <span className="uapi-system-chip">{s.payloads} imported</span>
                                        )}
                                        {s.size ? <span className="lu-import-size">{kb(s.size)}</span> : null}
                                    </span>
                                </label>
                            ))}
                        </div>
                        <div className="lu-import-footer">
                            <button type="button" className="pp-criteria-add-btn" onClick={onClose} disabled={busy}>Cancel</button>
                            <button type="button" className="pp-rules-add-btn" onClick={readSpec} disabled={busy || !spec}>
                                {busy ? `Reading ${spec?.name}…` : 'Next'}
                            </button>
                        </div>
                    </>
                )}

                {step === 2 && read && (
                    <>
                        <div className="lu-import-head" style={{ borderTop: 'none', paddingTop: 0 }}>
                            {forOperation && (
                                <p className="uapi-wizard-target">
                                    Finding a body for{' '}
                                    <span className={`lu-method lu-method-${String(forOperation.method).toLowerCase()}`}>
                                        {forOperation.method}
                                    </span>{' '}
                                    <code className="lu-mono">{forOperation.full_path}</code>
                                </p>
                            )}
                            <p className="lu-import-sub">
                                The payloads {read.specFile} publishes — {read.payloads.length} of them
                                {read.specVersion ? `, version ${read.specVersion}` : ''}. Nothing has been
                                written yet.
                            </p>
                            <div className="uapi-search-row">
                                <Search size={15} className="uapi-search-icon" />
                                <input
                                    type="text"
                                    className="pst-hotel-search"
                                    placeholder="Search payloads…"
                                    value={payloadSearch}
                                    onChange={e => setPayloadSearch(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="lu-import-list">
                            {matchesPayload.length === 0 ? (
                                <p className="lu-empty" style={{ padding: '1.5rem' }}>No payload matches that.</p>
                            ) : matchesPayload.map(p => (
                                <label
                                    key={p.operation_path}
                                    className={`lu-import-row${picked === keyOf(p) ? ' picked' : ''}`}
                                >
                                    <input
                                        type="radio"
                                        name="uapi-payload"
                                        checked={picked === keyOf(p)}
                                        onChange={() => setPicked(keyOf(p))}
                                        disabled={busy}
                                    />
                                    <span className="lu-import-row-main">
                                        <span className="lu-import-op">
                                            {p.existing ? p.existing.name : (p.summary || p.operation_path)}
                                        </span>
                                        <span className="lu-import-summary">{p.operation_path}</span>
                                    </span>
                                    <span className="lu-import-meta">
                                        {/* Which end of the call this document describes. A body taken
                                            from a POST-only endpoint is its request, not a resource. */}
                                        {/* Which document this is. Both may be listed for one path now, and
                                            picking the wrong one is what refused every create on /rsv. */}
                                        <span className="uapi-system-chip">
                                            {p.body_source === 'request'
                                                ? `${p.source_method || 'write'} request body`
                                                : 'response body'}
                                        </span>
                                        {p.existing && <span className="uapi-system-chip">imported</span>}
                                        {String(p.applies_to || '').split(',').filter(Boolean).map(m => (
                                            <span key={m} className={`lu-method lu-method-${m.toLowerCase()}`}>{m}</span>
                                        ))}
                                        <span className="lu-import-size">{kb(p.schema_bytes)}</span>
                                    </span>
                                </label>
                            ))}
                        </div>
                        <div className="lu-import-footer">
                            <button type="button" className="pp-criteria-add-btn" onClick={() => setStep(1)} disabled={busy}>Back</button>
                            <button type="button" className="pp-rules-add-btn" onClick={goToUrls} disabled={busy || !picked}>
                                Next
                            </button>
                        </div>
                    </>
                )}

                {step === 3 && read && chosen && (
                    <>
                        <div className="lu-import-head" style={{ borderTop: 'none', paddingTop: 0 }}>
                            <p className="lu-import-sub">
                                Which of our Universal API calls carry <strong>{chosen.operation_path}</strong>.
                                More than one is normal — the spec says this body serves{' '}
                                <strong>{String(chosen.applies_to || 'GET').split(',').join(', ')}</strong>.
                                {seedSource === 'operation'
                                    ? ' The URL you came from is ticked, with the matching methods on that path.'
                                    : ' Only the matching methods on that exact path are ticked; everything else'
                                      + ' is greyed and can still be ticked by hand.'}
                            </p>
                            {/* Saving REPLACES the mapping, so a URL this payload currently serves that is
                                no longer ticked will be dropped. Said before the button, not discovered
                                after it. */}
                            {(() => {
                                const already = (chosen.existing?.operation_ids || []).map(String);
                                const losing = already.filter(x => !urls.includes(x));
                                if (!losing.length) return null;
                                return (
                                    <p className="uapi-seed-reset">
                                        <span>
                                            This payload currently serves {losing.length} URL
                                            {losing.length === 1 ? '' : 's'} the spec does not match on this
                                            path. {losing.length === 1 ? 'It is' : 'They are'} unticked and
                                            will be removed when you save.
                                        </span>
                                        <button
                                            type="button"
                                            className="ppp-detail-btn"
                                            onClick={() => setUrls([...new Set([...urls, ...already])])}
                                        >
                                            Keep {losing.length === 1 ? 'it' : 'them'}
                                        </button>
                                    </p>
                                );
                            })()}
                            <div className="uapi-search-row">
                                <Search size={15} className="uapi-search-icon" />
                                <input
                                    type="text"
                                    className="pst-hotel-search"
                                    placeholder="Search our operations…"
                                    value={urlSearch}
                                    onChange={e => setUrlSearch(e.target.value)}
                                />
                            </div>
                        </div>
                        {/* The gap between what the spec publishes and what we expose. Shown above the
                            list because it is the answer to "my call isn't in here" — which is otherwise
                            a dead end at this step. */}
                        {missingMethods.length > 0 && (
                            <div className="uapi-mirror-box">
                                <div className="uapi-mirror-head">
                                    <strong>Not in our Universal API yet</strong>
                                    <span className="lu-muted">
                                        {read.specFile} publishes {missingMethods.join(', ')} on{' '}
                                        <code className="lu-mono">{chosen.operation_path}</code> and we do
                                        not expose it. Tick to create it here as a pass-through.
                                    </span>
                                </div>
                                <div className="uapi-mirror-methods">
                                    {missingMethods.map(m => {
                                        const on = mirror.includes(m);
                                        return (
                                            <label key={m} className={`uapi-mirror-method${on ? ' picked' : ''}`}>
                                                <input
                                                    type="checkbox"
                                                    checked={on}
                                                    onChange={() => setMirror(prev =>
                                                        prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m])}
                                                    disabled={busy}
                                                />
                                                <span className={`lu-method lu-method-${m.toLowerCase()}`}>{m}</span>
                                                <span className="lu-mono">{chosen.operation_path}</span>
                                            </label>
                                        );
                                    })}
                                </div>
                                {mirror.length > 0 && (
                                    <p className="lu-payload-hint">
                                        Saving creates {mirror.length === 1 ? 'this call' : `these ${mirror.length} calls`}
                                        {read.module?.path ? ` under ${read.module.path}` : ''} and maps this payload to
                                        {mirror.length === 1 ? ' it' : ' them'}. Existing routes are reused, never duplicated.
                                    </p>
                                )}
                            </div>
                        )}

                        <div className="lu-import-list">
                            {(read.ourOperations || []).length === 0 ? (
                                <p className="lu-empty" style={{ padding: '1.5rem' }}>
                                    The route tree has no operations yet. Import them from the spec on the
                                    Universal API page first.
                                </p>
                            ) : matchesUrl.length === 0 ? (
                                <p className="lu-empty" style={{ padding: '1.5rem' }}>No operation matches that.</p>
                            ) : matchesUrl.map(o => {
                                const id = String(o.id);
                                const checked = urls.includes(id);
                                // Greyed, not hidden and not disabled: the spec not matching is a strong
                                // hint and not a rule - OHIP publishes a body once and we may well carry
                                // it on a URL of our own shaping. Hiding them would make that mapping
                                // impossible; ticking them made every step look broken.
                                const dim = !isSpecMatch(o, chosen) && !checked;
                                return (
                                    <label key={id} className={`lu-import-row${checked ? ' picked' : ''}${dim ? ' dimmed' : ''}`}>
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() => setUrls(prev =>
                                                prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])}
                                            disabled={busy}
                                        />
                                        <span className="lu-import-row-main">
                                            <span className="lu-import-op">
                                                <span className={`lu-method lu-method-${String(o.method).toLowerCase()}`}>
                                                    {o.method}
                                                </span>
                                                {' '}{o.full_path}
                                            </span>
                                            {o.description && <span className="lu-import-summary">{o.description}</span>}
                                        </span>
                                    </label>
                                );
                            })}
                        </div>
                        <div className="lu-import-footer">
                            <button type="button" className="pp-criteria-add-btn" onClick={() => setStep(2)} disabled={busy}>Back</button>
                            {/* No minimum. A payload imported before the URL that will carry it exists is
                                a real state, and refusing to store it would mean importing it twice. */}
                            <button type="button" className="pp-rules-add-btn" onClick={goToName} disabled={busy}>
                                Next ({urls.length + mirror.length} URL{urls.length + mirror.length === 1 ? '' : 's'}
                                {mirror.length ? `, ${mirror.length} new` : ''})
                            </button>
                        </div>
                    </>
                )}

                {step === 4 && chosen && (
                    <>
                        <div className="lu-import-list" style={{ padding: '1.25rem 1.5rem' }}>
                            <label className="lu-cred-label" htmlFor="uapi-payload-name">Name</label>
                            <input
                                id="uapi-payload-name"
                                type="text"
                                className="form-input"
                                value={name}
                                maxLength={255}
                                onChange={e => setName(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter' && name.trim() && !busy) save(); }}
                                disabled={busy}
                                autoFocus
                            />
                            <p className="lu-payload-hint">
                                What this document is called everywhere else — "Reservation", "Guest profile",
                                "Activity booking". It has to be unique, and it is what the list is read by.
                            </p>

                            <WizardReview style={{ marginTop: '1rem' }} rows={[
                                    ['From spec', `${read.specFile}${read.specVersion ? ` · ${read.specVersion}` : ''}`],
                                    ['Body', chosen.operation_path],
                                    ['Serves', selectedUrls.length
                                        ? selectedUrls.map(o => `${o.method} ${o.full_path}`).join(', ')
                                        : (mirror.length ? '—' : 'nothing yet')],
                                    // Listed separately from "Serves" because these do not exist yet.
                                    // Saving is what creates them, and that should be visible before the
                                    // button is pressed rather than reported afterwards.
                                    ...(mirror.length
                                        ? [['Creates', mirror.map(m => `${m} ${chosen.operation_path}`).join(', ')]]
                                        : []),
                                    ['Module', read.module?.path || 'none of ours names this spec'],
                                    ['Size', kb(chosen.schema_bytes)],
                                ]} />
                            {chosen.existing && (
                                <p className="lu-payload-hint" style={{ marginTop: '0.75rem' }}>
                                    This body is already imported as <strong>{chosen.existing.name}</strong>.
                                    Saving refreshes it from the spec and replaces its name and URLs — it does
                                    not make a second copy.
                                </p>
                            )}
                        </div>
                        <div className="lu-import-footer">
                            <button type="button" className="pp-criteria-add-btn" onClick={() => setStep(3)} disabled={busy}>Back</button>
                            <button type="button" className="pp-rules-add-btn" onClick={save} disabled={busy || !name.trim()}>
                                {busy ? 'Saving…' : 'Save payload'}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
